import React, { ReactElement, useEffect, useState } from "react";
import {
	Badge,
	Form,
	FormControl,
	ListGroup,
	Tab,
	Tabs,
} from "react-bootstrap";
import {
	faCheckCircle,
	faClock,
	faExclamationTriangle,
	faEye,
	faFolder,
	faInbox,
	faListCheck,
	faCalendarDay,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Project, ProjectService, ProjectTaskList, ProjectTaskService } from ".";
import { Greeting, Loading } from "../ui";
import { useUsers } from "../users";
import { ProjectTask, ProjectTaskStatus } from "./ProjectTask";

interface ITasksBtStatus {
	name: string;
	tasks: ProjectTask[];
}

export const MyProjectsListPage = (): ReactElement => {
	const [projects, setProjects] = useState<Project[]>();
	const [completedProjects, setCompletedProjects] = useState<Project[]>();
	const [tasksToStartToday, setTasksToStartToday] = useState<ProjectTask[]>();

	const [tasksByStatus, setTasksByStatus] = useState<ITasksBtStatus[]>([]);

	const [tasksDueSoon, setTasksDueSoon] = useState<ProjectTask[]>();
	const [tasksOverdue, setTasksOverdue] = useState<ProjectTask[]>();
	const [tasksWatching, setTasksWatching] = useState<ProjectTask[]>();

	const [projectSearchTerm, setProjectSearchTerm] = useState<string>("");

	const userContext = useUsers();

	const taskService = new ProjectTaskService();
	const projectService = new ProjectService();

	useEffect(() => {
		const load = async () => {
			const statuses = [
				ProjectTaskStatus.InProgress,
				ProjectTaskStatus.InternalReview,
				ProjectTaskStatus.ClientReview,
				ProjectTaskStatus.OnHold,
				ProjectTaskStatus.NotStarted,
			];

			const [
				completed,
				startToday,
				dueSoon,
				overdue,
				watching,
				statusGroups,
			] = await Promise.all([
				projectService.findMineCompleted(),
				taskService.findTasksToStartToday(),
				taskService.findTasksDueSoonForUser(),
				taskService.findTasksOverdueForUser(),
				taskService.findTasksWatched(),
				Promise.all(
					statuses.map(async status => ({
						name: status,
						tasks: await taskService.findTasksInStatusForUser(
							status,
						),
					})),
				),
			]);

			setCompletedProjects(completed);
			setTasksToStartToday(startToday);
			setTasksDueSoon(dueSoon);
			setTasksOverdue(overdue);
			setTasksWatching(watching);
			setTasksByStatus(statusGroups);
		};

		load();
	}, []);

	useEffect(() => {
		const fetchProjects = async () => {
			const projectData = await projectService.findMineOpen(
				projectSearchTerm,
			);
			setProjects(projectData);
		};

		fetchProjects();
	}, [projectSearchTerm]);

	// Tabs state
	const [taskTab, setTaskTab] = useState<string>("inProgress");
	const [selectedStatus, setSelectedStatus] = useState<string>(
		ProjectTaskStatus.InProgress,
	);

	// KPI counts for summary cards
	const overdueCount = tasksOverdue ? tasksOverdue.length : 0;
	const dueSoonCount = tasksDueSoon ? tasksDueSoon.length : 0;
	const startTodayCount = tasksToStartToday ? tasksToStartToday.length : 0;
	const watchingCount = tasksWatching ? tasksWatching.length : 0;
	const inProgressTasks = tasksByStatus.find(
		(s) => s.name === ProjectTaskStatus.InProgress,
	)?.tasks;
	const inProgressCount = inProgressTasks ? inProgressTasks.length : 0;
	const openProjectsCount = projects ? projects.length : 0;
	const completedProjectsCount = completedProjects
		? completedProjects.length
		: 0;

	// Render open projects list
	let openProjectsList = null;
	if (!projects) {
		openProjectsList = <Loading />;
	} else if (projects.length === 0) {
		openProjectsList = <div className="text-muted">No open projects.</div>;
	} else {
		openProjectsList = (
			<ListGroup className="my-projects-list">
				{projects.map((project: Project, index: number) => {
					const stats = project.userStats;
					return (
						<ListGroup.Item key={index} action href={project.dashboardLink}>
							<span className="project-name">{project.name}</span>
							<div className="project-badges">
								<Badge bg="danger" title="Tasks overdue">
									{stats ? stats.totalTasksOverdue : 0}
								</Badge>
								<Badge bg="info" title="Tasks due today">
									{stats ? stats.totalTasksDueToday : 0}
								</Badge>
							</div>
						</ListGroup.Item>
					);
				})}
			</ListGroup>
		);
	}

	// Render completed projects list
	let completedProjectsList = null;
	if (!completedProjects) {
		completedProjectsList = <Loading />;
	} else if (completedProjects.length === 0) {
		completedProjectsList = (
			<div className="text-muted">No completed projects.</div>
		);
	} else {
		completedProjectsList = (
			<ListGroup className="my-projects-list">
				{completedProjects.map((project: Project, index: number) => (
					<ListGroup.Item key={index} action href={project.dashboardLink}>
						<span className="project-name">{project.name}</span>
					</ListGroup.Item>
				))}
			</ListGroup>
		);
	}

	return (
		<div id="my-projects-page">
			{/* Header Card */}
			<div className="my-projects-header-card">
				<div className="my-projects-header-main">
					<div className="my-projects-icon-wrapper">
						<FontAwesomeIcon icon={faListCheck} />
					</div>
					<div className="my-projects-info">
						<h3>
							<Greeting />,{" "}
							{userContext.user ? userContext.user.firstName : ""}!
						</h3>
						<p>Focus on what matters most today.</p>
					</div>
				</div>
				<div className="my-projects-header-actions">
					<FormControl
						placeholder="Filter projects..."
						onChange={(e: any) => setProjectSearchTerm(e.target.value)}
					/>
				</div>
			</div>

			{/* Stats Grid */}
			<div className="my-projects-stats-grid">
				<div className="my-projects-stats-card stat-danger">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faExclamationTriangle} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Overdue</span>
						<span className="stats-value">{overdueCount}</span>
					</div>
				</div>
				<div className="my-projects-stats-card stat-warning">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faClock} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Due Soon</span>
						<span className="stats-value">{dueSoonCount}</span>
					</div>
				</div>
				<div className="my-projects-stats-card stat-info">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faCalendarDay} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Start Today</span>
						<span className="stats-value">{startTodayCount}</span>
					</div>
				</div>
				<div className="my-projects-stats-card stat-secondary">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faEye} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Watching</span>
						<span className="stats-value">{watchingCount}</span>
					</div>
				</div>
				<div className="my-projects-stats-card stat-primary">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faFolder} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Open Projects</span>
						<span className="stats-value">{openProjectsCount}</span>
					</div>
				</div>
				<div className="my-projects-stats-card stat-success">
					<div className="stats-icon">
						<FontAwesomeIcon icon={faCheckCircle} />
					</div>
					<div className="stats-content">
						<span className="stats-label">Completed</span>
						<span className="stats-value">{completedProjectsCount}</span>
					</div>
				</div>
			</div>

			{/* Content Grid */}
			<div className="my-projects-content-grid">
				{/* Task Inbox Card */}
				<div className="my-projects-content-card">
					<div className="my-projects-content-header">
						<FontAwesomeIcon icon={faInbox} />
						Task Inbox
					</div>
					<div className="my-projects-content-body">
						<Tabs
							activeKey={taskTab}
							onSelect={key => setTaskTab(key || "inProgress")}
							mountOnEnter
							unmountOnExit
						>
							<Tab eventKey="inProgress" title={`In Progress (${inProgressCount})`}>
								{tasksByStatus.length === 0 ? (
									<Loading />
								) : (
									<ProjectTaskList
										tasks={inProgressTasks || []}
										emptyMessage="You currently do not have any tasks In Progress."
									/>
								)}
							</Tab>
							<Tab eventKey="overdue" title={`Overdue (${overdueCount})`}>
								{typeof tasksOverdue === "undefined" ? (
									<Loading />
								) : (
									<ProjectTaskList
										tasks={tasksOverdue}
										emptyMessage="You currently do not have any tasks overdue."
									/>
								)}
							</Tab>
							<Tab eventKey="dueSoon" title={`Due Soon (${dueSoonCount})`}>
								{typeof tasksDueSoon === "undefined" ? (
									<Loading />
								) : (
									<ProjectTaskList
										tasks={tasksDueSoon}
										emptyMessage="You currently do not have any tasks due soon."
									/>
								)}
							</Tab>
							<Tab eventKey="startToday" title={`Start Today (${startTodayCount})`}>
								{typeof tasksToStartToday === "undefined" ? (
									<Loading />
								) : (
									<ProjectTaskList
										tasks={tasksToStartToday}
										emptyMessage="You currently do not have any tasks to start today."
									/>
								)}
							</Tab>
							<Tab eventKey="status" title="By Status">
								{tasksByStatus.length === 0 ? (
									<Loading />
								) : (
									<>
										<Form.Select
											value={selectedStatus}
											onChange={e => setSelectedStatus(e.target.value)}
											className="mb-3"
										>
											{tasksByStatus.map((s, idx) => (
												<option key={idx} value={s.name}>
													{s.name}
												</option>
											))}
										</Form.Select>
										<ProjectTaskList
											tasks={
												(tasksByStatus.find(s => s.name === selectedStatus)?.tasks) || []
											}
											emptyMessage={`You currently do not have any tasks in ${selectedStatus}`}
										/>
									</>
								)}
							</Tab>
						</Tabs>
					</div>
				</div>

				{/* Projects Card */}
				<div className="my-projects-content-card">
					<div className="my-projects-content-header">
						<FontAwesomeIcon icon={faFolder} />
						Projects
					</div>
					<div className="my-projects-content-body">
						<Tabs defaultActiveKey="open" mountOnEnter unmountOnExit>
							<Tab eventKey="open" title={`Open (${openProjectsCount})`}>
								{openProjectsList}
							</Tab>
							<Tab eventKey="completed" title={`Completed (${completedProjectsCount})`}>
								{completedProjectsList}
							</Tab>
						</Tabs>
					</div>
				</div>
			</div>
		</div>
	);
};
